import type { FC } from "react";

import type {
  RepositoryIndexingInsight,
  RepositoryIndexingRun,
  SourceControlRepository,
} from "../../types";

import { formatRelativeTime } from "../../utils/time";
import { ZapIcon } from "../common/icons";
import { MetricCard } from "../common/MetricCard";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";
import { SummaryCard } from "../dashboard/SummaryCard";

interface IndexingSummaryCardsProps {
  repositories: SourceControlRepository[];
  runs: RepositoryIndexingRun[];
  insights: RepositoryIndexingInsight[];
  loading?: boolean;
  scope?: string | null;
}

function resolveInsightsDescription(
  hasRuns: boolean,
  scope: string | null | undefined,
): string {
  if (!hasRuns) {
    return "Select a run to view";
  }
  if (scope) {
    return `From selected run & ${scope}`;
  }
  return "From selected run";
}

export const IndexingSummaryCards: FC<IndexingSummaryCardsProps> = ({
  repositories,
  runs,
  insights,
  scope,
}) => {
  const hasRuns = runs.length > 0;
  const latestRun = hasRuns ? runs[0] : null;

  const successRate = hasRuns
    ? Math.round(
        (runs.filter((r) => r.status === "completed").length / runs.length) *
          100,
      )
    : 0;

  const insightsDescription = resolveInsightsDescription(hasRuns, scope);

  return (
    <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <SummaryCard
        title="Repositories"
        value={repositories.length}
        description="Tracked repositories"
        icon={
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
        }
      />

      <MetricCard
        label="Latest Run"
        className="!border-slate-300 !bg-white transition-colors hover:!border-slate-400 dark:!border-slate-700 dark:!bg-arti-panel"
        value={
          <div className="flex items-start justify-between gap-3 font-normal">
            <div>
              {hasRuns && latestRun ? (
                <>
                  <StatusBadge status={latestRun.status} className="mt-1" />
                  <Text as="p" className="mt-2">
                    {latestRun.started_at
                      ? formatRelativeTime(latestRun.started_at)
                      : "Not started"}
                  </Text>
                </>
              ) : (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No runs yet
                </p>
              )}
            </div>
            <div className="rounded-none border border-slate-300 bg-slate-50 p-2 text-gray-600 dark:border-slate-700 dark:bg-arti-background dark:text-gray-400">
              <ZapIcon size={20} />
            </div>
          </div>
        }
      />

      <SummaryCard
        title="Insights"
        value={insights.length}
        description={insightsDescription}
        icon={
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        }
      />

      <SummaryCard
        title="Success Rate"
        value={successRate}
        description={`${runs.filter((r) => r.status === "completed").length} of ${runs.length} runs completed`}
        icon={
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
      />
    </section>
  );
};
